import React from 'react';

import {
  ParagraphItemsFragment,
  useCreateParagraphItemMutation,
  useDeleteParagraphItemMutation,
  useParagraphQuery,
  useUpdateParagraphItemMutation,
} from '../../../generated/graphql';
import deletePageItem from '../../../helpers/apolloCache/deletePageItem';
import { useManageItem } from '../../../helpers/useManageItem';
import Layout from '../../FormComponents/Layout';
import Form, { Attributes, InitialValues, initialValues, validationSchema } from './Form';
import addItemToPage from '../../../helpers/apolloCache/addItemToPage';
import { PageItemProps } from '../BasicItem';
import { pagesQuery } from '../../LeftContainer/graphql';

type ParagraphItem = ParagraphItemsFragment | undefined;

export const ParagraphItem = ({ onSaveHandler }: PageItemProps) => {
  const { pageId, itemId, isNew, close } = useManageItem();
  const [update, { loading: updating }] = useUpdateParagraphItemMutation();
  const [create, { loading: creating }] = useCreateParagraphItemMutation();
  const [destroy, { loading: deleting }] = useDeleteParagraphItemMutation();

  const { data, loading } = useParagraphQuery({
    variables: {
      pageId,
      id: itemId!,
    },
    skip: isNew,
  });

  const item = data?.page?.paragraphItem;

  const save = async (values: InitialValues): Promise<void> => {
    let attrs: Attributes = {
      pageId,
      content: values.content || '',
    };

    if (item?.id) {
      await update({
        variables: {
          attributes: {
            ...attrs,
            id: item.id,
          },
        },
        update: (_cache, { data }) => close(data?.updateParagraphItem),
      });
    } else {
      await create({
        variables: { attributes: attrs },
        update: (cache, { data }) => {
          addItemToPage<ParagraphItemsFragment | undefined>(cache, pageId, data?.createParagraphItem, close);
        },
        refetchQueries: [{ query: pagesQuery }],
      });
    }
    if (onSaveHandler) {
      onSaveHandler();
    }
  };

  const onDelete = async () => {
    if (itemId) {
      await destroy({
        variables: {
          attributes: {
            id: itemId,
            pageId,
          },
        },
        update(cache) {
          deletePageItem({ cache, pageId, itemId });
        },
        refetchQueries: [{ query: pagesQuery }],
      });
    }
  };

  return (
    <Layout
      headerText={`${isNew ? 'Add' : 'Update'} a Text Block`}
      validationSchema={validationSchema}
      initialValues={initialValues(item)}
      onSubmit={save}
      Component={Form}
      loading={loading}
      updating={updating || creating || deleting}
      close={close}
      pageId={pageId}
      isNew={isNew}
      onDelete={onDelete}
    />
  );
};
